import React from "react";
import { motion } from "framer-motion";
import { Drop, Fire, Waves, Sun } from "@phosphor-icons/react";

const CHARCOAL_BG = "https://customer-assets.emergentagent.com/job_exfoliating-bars/artifacts/8s5lbosg_Photo%20Jan%2027%202026%2C%203%2053%2018%20PM.jpg";
const SAND_BG = "https://customer-assets.emergentagent.com/job_exfoliating-bars/artifacts/cwzmetsg_Photo%20Jan%2027%202026%2C%203%2059%2043%20PM.jpg";
const BREAKDOWN_IMG = "https://customer-assets.emergentagent.com/job_exfoliating-bars/artifacts/8clrjqir_ChatGPT%20Image%20Feb%2016%2C%202026%2C%2001_21_16%20AM.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const ingredients = [
  {
    icon: Fire,
    title: "Activated Charcoal",
    desc: "Draws out toxins and impurities from deep within your pores. Nature's detox for your skin.",
  },
  {
    icon: Waves,
    title: "Real Beach Sand",
    desc: "Not synthetic beads. Not plastic microparticles. Actual beach sand that scrubs like nothing else.",
  },
  {
    icon: Drop,
    title: "Menthol Cooling",
    desc: "The Split Scrub's moisturizing side delivers an icy-cool sensation that soothes freshly exfoliated skin.",
  },
  {
    icon: Sun,
    title: "Natural Moisturizers",
    desc: "Packed with hydrating ingredients to replenish your skin after the deepest scrub of its life.",
  },
];

export default function BrandStory() {
  return (
    <section id="story" className="py-24 lg:py-32" data-testid="brand-story-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-20"
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            className="font-accent text-sm tracking-[0.25em] uppercase mb-4"
            style={{ color: '#FF5A36' }}
          >
            What's Inside
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight"
            style={{ color: '#F0EDE5' }}
          >
            Built Different. Literally.
          </motion.h2>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          {/* Large image tile */}
          <motion.div
            variants={fadeUp}
            custom={0}
            className="md:col-span-7 relative overflow-hidden min-h-[400px]"
          >
            <img
              src={CHARCOAL_BG}
              alt="Charcoal texture"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/70" />
            <div className="relative z-10 p-10 lg:p-14 flex flex-col justify-end h-full">
              <p className="font-accent text-xs tracking-[0.3em] uppercase mb-3" style={{ color: '#A8C6C3', textShadow: '0 0 15px rgba(0,0,0,0.8)' }}>
                Our Philosophy
              </p>
              <h3 className="font-heading text-3xl lg:text-4xl font-light text-white tracking-tight mb-4" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.7)' }}>
                No synthetic scrubs.<br />No microplastics.
              </h3>
              <p className="font-body text-sm leading-relaxed max-w-md" style={{ color: 'rgba(240,237,229,0.9)', textShadow: '0 2px 10px rgba(0,0,0,0.9)' }}>
                We use what the ocean gives us. Real beach sand and activated charcoal for an exfoliation
                that commercial soaps can't replicate.
              </p>
            </div>
          </motion.div>

          {/* Sand image tile */}
          <motion.div
            variants={fadeUp}
            custom={1}
            className="md:col-span-5 relative overflow-hidden min-h-[400px]"
          >
            <img
              src={SAND_BG}
              alt="Sand pattern"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/65" />
            <div className="relative z-10 p-10 lg:p-14 flex flex-col justify-end h-full">
              <p className="font-heading text-5xl lg:text-6xl font-light text-white tracking-tight" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.7)' }}>
                $8
              </p>
              <p className="font-body text-sm mt-2" style={{ color: 'rgba(240,237,229,0.9)', textShadow: '0 2px 10px rgba(0,0,0,0.9)' }}>
                Per bar, shipped free. <br />Same quality, no markup.
              </p>
            </div>
          </motion.div>

          {/* Ingredient cards */}
          {ingredients.map((item, index) => (
            <motion.div
              key={item.title}
              variants={fadeUp}
              custom={index + 2}
              className="md:col-span-3 p-8 border"
              style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}
            >
              <item.icon size={28} weight="regular" style={{ color: '#FF5A36' }} className="mb-5" />
              <h4 className="font-heading text-xl font-medium mb-3" style={{ color: '#F0EDE5' }}>
                {item.title}
              </h4>
              <p className="font-body text-sm leading-relaxed" style={{ color: '#9A9A9A' }}>
                {item.desc}
              </p>
            </motion.div>
          ))}

          {/* Product Breakdown Image */}
          <motion.div
            variants={fadeUp}
            custom={6}
            className="md:col-span-12 relative overflow-hidden"
          >
            <img
              src={BREAKDOWN_IMG}
              alt="Split Scrub Bar layer breakdown"
              className="w-full max-h-[600px] object-contain"
              data-testid="product-breakdown-image"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
